import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiArrowLeft, FiCalendar } = FiIcons;

const ArticleGrandEcartPrix = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-52 pb-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/ressources"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
            Retour aux ressources
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            RNCP37275 : le grand écart des prix sur le marché de la formation de formateurs
          </h1>
          <div className="flex items-center text-gray-500 text-sm">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Mis en ligne le 18/03/2026</span>
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Dans un marché de la formation professionnelle en pleine structuration, la certification RNCP37275 – Formateur Professionnel d'Adultes s'impose comme un standard incontournable. Reconnue par l'État, elle atteste de compétences complètes en ingénierie pédagogique, animation et accompagnement des apprenants. Mais derrière cette reconnaissance commune, les prix affichés par les organismes de formation varient fortement, révélant un marché à plusieurs vitesses.
            </p>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Des formations "low cost" à moins de 2 500 €
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Première catégorie : les offres d'entrée de gamme, souvent 100 % à distance. Certains organismes proposent des tarifs très agressifs autour de 1 990 € à 2 500 €, comme Oravendis (1 990 € en promotion) ou Kine Formations (2 500 €).
                </p>
                <p>
                  Ces formations séduisent par leur accessibilité financière et leur flexibilité. Elles s'adressent principalement à des profils autonomes, capables de travailler en e-learning avec un accompagnement limité. Le revers est connu : moins de suivi individualisé, peu de mise en situation réelle, et une professionnalisation parfois superficielle.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le cœur du marché : entre 3 500 € et 5 500 €
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  La majorité des organismes sérieux se positionne dans une fourchette médiane comprise entre 3 500 € et 5 500 €. On y retrouve par exemple :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>MBL Formation : environ 3 700 à 3 990 €</li>
                  <li>Educatel / ESECAD : autour de 3 690 €</li>
                  <li>Kwark ou ISPn : environ 4 000 € à 5 000 €</li>
                  <li>Olann Formation : 5 544 €</li>
                </ul>
                <p>
                  Ce segment constitue aujourd'hui le standard du marché. Il combine généralement un accompagnement structuré, des temps de pratique, un suivi pédagogique réel et une préparation sérieuse à l'examen. C'est dans cette zone que se joue l'équilibre entre accessibilité financière et qualité.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le haut de gamme : jusqu'à 9 500 €
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  À l'autre extrémité, certaines formations atteignent des niveaux élevés, notamment dans les structures publiques ou très encadrées. L'AFPA affiche par exemple un tarif d'environ 9 548 €, tandis que certains GRETA ou dispositifs complets montent jusqu'à 6 700 €.
                </p>
                <p>
                  Ces offres se caractérisent par des parcours longs (souvent 800 à 900 heures), une forte présence en centre et un encadrement institutionnel. Elles visent un public en reconversion complète, mais peuvent apparaître surdimensionnées pour des professionnels déjà en activité.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un marché éclaté… et une question centrale : le bon positionnement
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  L'analyse est claire : le marché du RNCP37275 est polarisé entre low cost digitalisé et parcours lourds institutionnels.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Trop bas prix :</strong> risque de formation "théorique" sans réelle employabilité</li>
                  <li><strong>Trop élevé :</strong> investissement difficilement rentable, surtout pour des indépendants ou organismes de formation</li>
                </ul>
                <p>
                  Entre les deux, une zone médiane émerge comme la plus pertinente.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le bon compromis : prix, accompagnement et employabilité
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  C'est précisément dans cet espace que se positionnent certains acteurs privés structurés, à l'image du groupe Advocaci et de sa marque Institut Supérieur des Métiers de la Formation. Leur proposition s'inscrit dans une logique de juste prix, avec un accompagnement individualisé, une approche orientée activité (formateur, consultant, OF) et une logique de retour sur investissement rapide.
                </p>
                <p className="font-semibold text-gray-900 bg-blue-50 p-4 rounded-lg mt-4">
                  Dans un marché devenu concurrentiel, le critère décisif n'est plus seulement le prix affiché, mais le rapport entre coût, accompagnement et capacité réelle à exercer le métier. Pour le RNCP37275, la stratégie la plus rationnelle n'est ni le low cost, ni le premium institutionnel, mais un positionnement intermédiaire maîtrisé là où se construit aujourd'hui la véritable valeur professionnelle.
                </p>
              </div>
            </div>
          </div>

          {/* Back to resources */}
          <div className="text-center pt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              Retour aux ressources
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArticleGrandEcartPrix;
